import { useFormik } from "formik";
import { useAlert } from "hooks/useAlert";
import { useAuth } from "hooks/useAuth"
import { useSession } from "hooks/useSession";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import { useParams } from "react-router";
import { checkChangeDeep } from "utils/checkChange";
import * as Yup from 'yup'

interface ParamsProps {
  id: string;
}

export interface ContactProps {
  sellerContactId: number,
  contactTypeId: string,
  contact?: string,
  title?: string,
}

interface ContactTypeProps {
  contactTypeId: string,
  contactType?: string,
}

interface InputsProps {
  contacts: ContactProps[],
  socialMedias: ContactProps[],
}

const DEFAULT_INPUTS:InputsProps = {
  contacts: [],
  socialMedias: [],
}

export const useContacts = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [initialInputsValues, setInitialInputsValues] = useState<InputsProps>(DEFAULT_INPUTS);
  const [contactTypes, setContactTypes] = useState<ContactTypeProps[]>();
  const [socialMediaTypes, setSocialMediaTypes] = useState<ContactTypeProps[]>();
  const [changedFields, setChangedFields] = useState<string[]>([]);
  const { id } = useParams<ParamsProps>();
  const { setToast } = useToast();
  const { alert, setTextAlertInterface, openAlert, closeAlert, caller } = useAlert();
  const { connectApi, connectAllApi } = useAuth();
  const {handleSubmit, handleChange, setValues: setInputsValues, values: inputsValues, touched, errors} = useFormik({
    initialValues: DEFAULT_INPUTS,
    validationSchema: Yup.object({
      contacts: Yup.array().of(
        Yup.object({
          contactTypeId: Yup.string(),
          contact: Yup.string(),
          title: Yup.string(),
        })
      ).min(0),
      socialMedias: Yup.array().of(
        Yup.object({
          contactTypeId: Yup.string(),
          contact: Yup.string(),
        })
      ),
    }),
    onSubmit: (values) => {
      sendData(values);
    }
  });

  /* HOOKS */
  useEffect(() => {
    getData();
  }, [])

  useEffect(() => {
    const changed = checkChangeDeep(initialInputsValues, inputsValues);
    setChangedFields(changed);
  }, [inputsValues])

  useEffect(() => {
    if (caller === 'contact') {
      alert.typeContinue === "save" && saveFormChanges();
      alert.typeContinue === "discard" && discardChanges();
    }
  }, [alert])

  /* FORM SUBMISSION HANDLERS */
  const saveFormChanges = () => {
    handleSubmit();
    if (Object.keys(errors).length > 0) {
      setToast({
        message: `Favor corrigir o(s) campo(s) indicado(s)`,
        isActive: true,
        type: 'error',
        timeToRemoveToast: 3000
      })
      closeAlert();
      return;
    }
  }
  
  const discardChanges = () => {
    setInputsValues({...initialInputsValues});
    closeAlert();
  }

  /* HANDLERS */
  const handleAddContact = () => {
    const newContact:ContactProps = {
      sellerContactId: 0,
      contactTypeId: '',
      title: '',
      contact: '',
    }
    setInputsValues({
      ...inputsValues,
      contacts: [
        ...inputsValues.contacts,
        newContact,
      ]
    })
  }

  const handleRemoveContact = (contactIndex: number) => {
    const newContacts = [...inputsValues.contacts];
    newContacts.splice(contactIndex, 1);

    setInputsValues({
      ...inputsValues,
      contacts: newContacts,
    })
  }

  const handleFormDiscard = () => {
    showAlertForm(false)
  }

  const handleFormSubmit = () => {
    showAlertForm(true)
  }

  /* HELPERS */
  const showAlertForm = (typeOpen: boolean) => {
    setTextAlertInterface({
      positionCenter: false,
    })
    openAlert(typeOpen, 'contact');
  }

  /* API CALLS */
  const getData = async () => {
    setLoading(true);
    connectAllApi([
      {type: 'get', url: `sellers/${id}/contacts`},
      {type: 'get', url: `sellers/properties`}
    ]).then((responses) => {
      if(responses[0].status === "fulfilled" && responses[1].status === "fulfilled"){
        setHasError(false);
        const contactsDecoded:ContactProps[] = responses[0].value.data.contacts;
        const socialMediasDecoded:ContactProps[] = responses[0].value.data.socialMedias;

        const socialMediaTypesDecoded:ContactTypeProps[] = responses[1].value.data?.contactSocialMediaType;
        const contactTypesDecoded:ContactTypeProps[] = responses[1].value.data?.contactType;
        const inputsValues:InputsProps = {
          contacts: contactsDecoded.map((contact) => {
            return {
              sellerContactId: contact.sellerContactId,
              contactTypeId: contact.contactTypeId.toString(),
              contact: contact.contact ?? '',
              title: contact.title ?? '',
            }
          }),
          socialMedias: socialMediaTypesDecoded.map((socialMediaType) => {
            const socialMedia = socialMediasDecoded.find((socialMedia) => socialMedia.contactTypeId === socialMediaType.contactTypeId)
            return {
              sellerContactId: socialMedia?.sellerContactId ?? 0,
              contactTypeId: socialMediaType.contactTypeId.toString(),
              contact: socialMedia?.contact ?? '',
            }
          }),
        }
        setInitialInputsValues({...inputsValues});
        setInputsValues({...inputsValues});
        setContactTypes(contactTypesDecoded);
        setSocialMediaTypes(socialMediaTypesDecoded);
      }
    })
    .catch((_) => setHasError(true))
    .finally(() => setLoading(false));
  }

  const sendData = async (values: InputsProps) => {
    connectApi({
      type: 'put',
      url: `sellers/${id}/contacts`,
      body: values,
    }).then((_) => {
      getData();
    }).finally(() => closeAlert());;
  }

  return {
    loading,
    hasError,
    inputsValues,
    contactTypes,
    socialMediaTypes,
    changedFields,
    touched,
    errors,
    handleChange,
    handleAddContact,
    handleRemoveContact,
    handleFormSubmit,
    handleFormDiscard,
  }
}